import { defineStore } from 'pinia';
import { doc, getDoc, getDocFromCache, getDocFromServer, updateDoc, setDoc, onSnapshot, collection, query, where, getDocs, Timestamp, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '~/firebaseInstance';
import { useApi } from '~/composables/useApi';
import { onValue, ref as rtdbRef } from 'firebase/database';
import { rtdb } from '~/firebaseInstance';
import axios from 'axios';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/nuxt';

// Renew a shop's JWT once its remaining lifetime falls below this. Owners open
// the mobile app rarely, so we renew aggressively on every load to keep each
// linked shop's session from expiring. Mirrors the desktop POS renew flow
// (app/stores/auth.ts) but runs per-shop across all linked branches.
const SHOP_TOKEN_RENEW_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const decodeJwtExp = (token: string | null): number | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

// ms until the JWT expires. null = not a JWT / no exp claim (legacy token).
const msUntilExpiry = (token: string | null): number | null => {
  const exp = decodeJwtExp(token);
  return exp === null ? null : exp * 1000 - Date.now();
};

// Loading-screen time above which init() reports its step timings to Sentry.
// Sentry is errors-only here, so this is a single warning event for slow
// loads only — never a transaction/trace.
const SLOW_INIT_REPORT_MS = 5000;

const reportSlowInit = (marks: Record<string, number>, shopCount: number) => {
  console.info('[owner-v2] init timings (ms)', marks);
  if ((marks.total ?? 0) < SLOW_INIT_REPORT_MS || !Sentry.isInitialized()) return;
  Sentry.captureMessage('owner-v2 slow init', {
    level: 'warning',
    tags: { area: 'owner-v2-init' },
    extra: { ...marks, shopCount, online: navigator.onLine, connection: (navigator as any).connection?.effectiveType },
  });
};

// POS role values returned by /users/login (user.Role)
//   2 = admin (access all)
//   1 = custom (gated by permission string — TODO: parse permissions; treat as admin for phase 1)
//   0 = restricted
export type PosRole = 0 | 1 | 2;

// Firestore record under owner-users/{uid}.shops[customerId]
interface ShopRecord {
  customerId: string;
  shopName?: string;
  username: string;
  role: PosRole;
  // Comma-separated permission keys from POS /users/login (user.AccessList).
  // Defaults to 'all' when the login response does not include one.
  accessList: string;
  token: string;
  linkedAt: Date;
  lastUsedAt?: Date;
}

interface OwnerUserData {
  uid: string;
  createdAt: Date;
  selectedCustomerId: string | null;
  shops: Record<string, ShopRecord>;
  // Owner-mobile UI language, persisted per user (cross-device). localStorage
  // remains the fast/offline copy; this is the durable one.
  locale?: 'th' | 'en';
  // Owner-chosen display order of linked shops, as an array of customerIds.
  // Only an ordering hint: ids that no longer exist are skipped, and shops not
  // listed here (e.g. just added) fall to the end in natural order. See the
  // `stores` getter for how it is applied.
  shopOrder?: string[];
}

interface Store {
  storeId: string;
  name: string;
  username: string;
  token: string;
  role: PosRole;
  accessList: string;
  needsReLink: boolean;
}

// Live table item interface
interface LiveTableItem {
  bill: number;
  comments: string;
  dc1: [number, number, number];
  discountedPrice: number;
  extendedPrice: number;
  isOpenMenu: boolean;
  name: string;
  price: number;
  quantity: number;
  subItemId: string;
  toppingsAmount: number;
  uuid: string;
}

// Live table billing interface
interface LiveTableBilling {
  cc: [number, number, number];
  dc1: number;
  dc2: [number, number, number];
  grandTotal: number;
  rounding: number;
  roundingMode: string;
  sc: [number, number, number];
  subTotal: number;
  vat: [number, number, number];
}

// Live table interface — V2 includes nickName for friendly display name
interface LiveTable {
  billing: LiveTableBilling;
  guests: number;
  items: LiveTableItem[];
  mode: number;
  name: string;
  nickName?: string;
  setting?: Record<string, unknown>;
  startDateTime: string;
  uuid: string;
}

// Live tables data structure (keyed by table name)
type LiveTablesData = Record<string, LiveTable>;

// Unsubscribe functions type
type UnsubscribeFn = () => void;

// Customer summary types
interface CustomerSummaryData {
  status?: string;
  grandTotal?: number;
  businessDate?: string;
  lastUpdate?: {
    type: string;
    seconds: number;
    nanoseconds: number;
  };
  [key: string]: unknown;
}

interface CustomerSummaryByDate {
  [date: string]: CustomerSummaryData | null;
}

interface CustomerSummaryResult {
  shopId: string;
  dateRange: {
    start: string;
    end: string;
    totalDays: number;
  };
  totalGrandTotal: number;
  datesWithData: number;
}

interface CustomerSummaryStoreState {
  summaryData: CustomerSummaryByDate;
  summaryResult: CustomerSummaryResult | null;
}

type CustomerSummaryState = Record<string, CustomerSummaryStoreState>;

// Per-store live aggregate written by the POS plugin (jobs.live.plugin.ts)
// at the doc customers/{storeId} field `live`.
export interface StoreLiveSummary {
  paid: number;
  pending: number;
  tables: number;
  bills: number;
  guests?: number;
  businessDate?: string;
  lastUpdated?: string;
}
export interface StoreLiveDoc {
  name?: string;
  live?: StoreLiveSummary;
  // 0 = default (calendar day). 1 = nightclub mode (subtract 7h before truncating to date)
  businessMode?: number | string;
  // Top-level open/close state written by openShop / closeShop in pos.shop.ts
  status?: string;
  opened?: string;
  closed?: string;
}

// Compute the business date for a shop. businessMode==1 offsets "now" by -7h
// so a transaction at 01:45 still belongs to the previous calendar date.
export const computeBusinessDate = (businessMode?: number | string, now: dayjs.Dayjs = dayjs()): string => {
  const mode = Number(businessMode) || 0;
  const d = mode === 1 ? now.add(-7, 'hour') : now;
  return d.format('YYYY-MM-DD');
};
type LiveSummariesState = Record<string, StoreLiveDoc>;

export interface GrandTotalStats {
  grandTotal: number;
  bills: number;
  voidedAmount: number;
  voidedBills: number;
}
const emptyGrandTotalStats = (): GrandTotalStats => ({ grandTotal: 0, bills: 0, voidedAmount: 0, voidedBills: 0 });

export const useOwnerListV2Store = defineStore('owner-v2', {
  state: () => ({
    userData: null as OwnerUserData | null,
    selectedStoreId: '',
    loaded: false,
    loading: false,
    liveTables: {} as LiveTablesData,
    liveTablesLoaded: false,
    liveTablesLoading: false,
    // The shop whose tables_v3 is currently being streamed. Decoupled from
    // selectedStoreId — only changes when the user taps a different dashboard
    // shop card. Stays alive across page navigation until that happens.
    subscribedTablesStoreId: '',
    _liveTablesUnsubscribers: {} as Record<string, UnsubscribeFn[]>,
    customerSummary: {} as CustomerSummaryState,
    customerSummaryLoading: false,
    // Per-store live aggregates from customers/{storeId}.live (multi-shop dashboard)
    liveSummaries: {} as LiveSummariesState,
    _liveSummaryUnsubs: {} as Record<string, UnsubscribeFn>,
    dashboardView: 'live' as 'live' | 'by_date' | 'by_month' | 'by_year',
    dashboardStartDate: '',
    dashboardEndDate: '',
    grandTotals: {} as Record<string, GrandTotalStats>,
    grandTotalsLoading: false,
    _grandTotalRefetchTimers: {} as Record<string, ReturnType<typeof setTimeout>>,
    _liveSummaryFirstSeen: {} as Record<string, boolean>,
  }),

  actions: {
    async init() {
      const t0 = performance.now();
      const marks: Record<string, number> = {};
      const mark = (step: string) => { marks[step] = Math.round(performance.now() - t0); };

      const fbAuth = useFirebaseAuth();
      await fbAuth.initAuth();
      mark('auth');

      if (!fbAuth.uid.value) {
        return navigateTo('/v2-owner-report-mobile/login');
      }

      await this.loadUserData();
      mark('userDoc');

      if (!this.selectedStoreId && this.stores.length > 0) {
        this.selectedStoreId = this.stores[0]!.storeId;
        // Not awaited: a server-acked write must not hold the loading screen.
        this.saveSelectedStore().catch((e) => console.warn('[owner-v2] saveSelectedStore failed', e));
      }

      if (this.selectedStoreId) {
        const { setCustomerId, init: initDataService } = useDataservice();
        this._applyShopAuth(this.selectedStoreId);
        setCustomerId(this.selectedStoreId);
        initDataService();
        // Do NOT subscribe tables_v3 here — defer until the user explicitly
        // enters a shop's Active Tables view (or switches shops).
      }

      this.subscribeAllStoresLiveSummary();

      // Keep every linked shop's session alive: renew any token under the
      // 7-day threshold and persist the refreshed JWTs. Fire-and-forget — the
      // current tokens are still valid, so dashboard navigation need not wait.
      void this.renewExpiringShopTokens();

      this.loaded = true;
      mark('total');
      reportSlowInit(marks, this.stores.length);

      if (this.stores.length === 0) {
        return navigateTo('/v2-owner-report-mobile/link-shop');
      }
      return navigateTo('/v2-owner-report-mobile/dashboard');
    },

    async loadUserData() {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) return;

      const userRef = doc(db, 'owner-users', fbAuth.uid.value);

      // Cache first: the linked-shop list rarely changes, and a server read
      // waits up to Firestore's 10s online-state timeout on a slow connection
      // (iOS Safari / cellular) before falling back to cache anyway. Only trust
      // a cached doc that carries `shops` — a cache holding just a local merge
      // (e.g. a locale write, no base doc) would otherwise read as "no shops"
      // and bounce the owner to link-shop.
      let fromCache = false;
      let snap;
      try {
        snap = await getDocFromCache(userRef);
        fromCache = snap.exists() && typeof snap.data()?.shops === 'object';
      } catch {
        // not cached (first visit on this device) / memory-only cache
      }
      if (!fromCache) snap = await getDoc(userRef);

      if (!snap?.exists()) return;
      this._applyUserDoc(snap.data());

      // Pick up changes made on another device (shop linked/removed, order,
      // locale) without holding the loading screen for them.
      if (fromCache) {
        getDocFromServer(userRef)
          .then((fresh) => {
            if (fresh.exists() && fbAuth.uid.value === fresh.id) this._applyServerUserDoc(fresh.data());
          })
          .catch((e) => console.warn('[owner-v2] user doc server refresh failed', e));
      }
    },

    _applyServerUserDoc(data: any) {
      const shopKey = () => this.stores.map(s => s.storeId).sort().join(',');
      const shopsBefore = shopKey();
      const selectedBefore = this.selectedStoreId;
      const localTokens: Record<string, string> = {};
      for (const shop of Object.values(this.userData?.shops ?? {})) localTokens[shop.customerId] = shop.token;

      this._applyUserDoc(data, { keepSelection: true });

      // A token renewed on this device may not have reached the server copy yet.
      for (const shop of Object.values(this.userData?.shops ?? {})) {
        const local = localTokens[shop.customerId];
        if (local && (decodeJwtExp(local) ?? 0) > (decodeJwtExp(shop.token) ?? 0)) shop.token = local;
      }

      // Before init() finishes it applies auth and subscribes from this data itself.
      if (!this.loaded) return;
      if (this.selectedStoreId && this.selectedStoreId !== selectedBefore) {
        this._applyShopAuth(this.selectedStoreId);
        useDataservice().setCustomerId(this.selectedStoreId);
      }
      if (shopKey() !== shopsBefore) this.subscribeAllStoresLiveSummary();
    },

    _applyUserDoc(data: any, opts: { keepSelection?: boolean } = {}) {
      const rawShops = (data.shops || {}) as Record<string, any>;
      const shops: Record<string, ShopRecord> = {};
      for (const [cid, s] of Object.entries(rawShops)) {
        shops[cid] = {
          customerId: cid,
          shopName: s.shopName,
          username: s.username,
          role: (s.role ?? 0) as PosRole,
          accessList: typeof s.accessList === 'string' && s.accessList ? s.accessList : 'all',
          token: s.token || '',
          linkedAt: s.linkedAt?.toDate?.() || new Date(),
          lastUsedAt: s.lastUsedAt?.toDate?.() || undefined,
        };
      }

      this.userData = {
        uid: data.uid,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        selectedCustomerId: data.selectedCustomerId || null,
        shops,
        locale: data.locale === 'th' || data.locale === 'en' ? data.locale : undefined,
        shopOrder: Array.isArray(data.shopOrder)
          ? data.shopOrder.filter((id: any): id is string => typeof id === 'string')
          : undefined,
      };
      // On a background refresh keep the shop already in use (its auth is
      // applied) unless it was unlinked on another device.
      if (!opts.keepSelection || !this.selectedStoreId || !shops[this.selectedStoreId]) {
        this.selectedStoreId = this.userData.selectedCustomerId || '';
      }

      // Cross-device locale: the user doc is authoritative. Sync it into the
      // localStorage-backed owner locale + the live i18n instance so this device
      // renders in the language the user picked elsewhere.
      if (this.userData.locale) {
        setOwnerLocale(this.userData.locale);
        try {
          const i18n: any = useNuxtApp().$i18n;
          if (typeof i18n?.setLocale === 'function') i18n.setLocale(this.userData.locale);
          else if (i18n?.locale && 'value' in i18n.locale) i18n.locale.value = this.userData.locale;
        } catch { /* i18n not ready — middleware/layout will apply from localStorage */ }
      }
    },

    /**
     * Persist the owner's chosen UI language to their user document
     * (owner-users/{uid}) — the same doc that holds their linked shops — so it
     * follows them across devices. Called alongside the localStorage write in
     * the Account-tab toggle.
     */
    async saveOwnerLocale(locale: 'th' | 'en') {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) return;
      if (this.userData) this.userData.locale = locale;
      try {
        const userRef = doc(db, 'owner-users', fbAuth.uid.value);
        await setDoc(userRef, { locale }, { merge: true });
      } catch (e) {
        console.error('[owner-v2] failed to persist locale to user doc', e);
      }
    },

    /**
     * Persist the owner's chosen shop display order to their user document
     * (owner-users/{uid}). `order` is an array of customerIds; unknown or stale
     * ids are harmless (the `stores` getter skips them). Mirrors the locale
     * write pattern — optimistic local update, then Firestore merge.
     */
    async saveShopOrder(order: string[]) {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) return;
      // Keep only ids that map to a currently-linked shop, preserving the
      // caller's order, so we never persist references to removed shops.
      const validOrder = this.userData
        ? order.filter(id => !!this.userData!.shops[id])
        : order;
      if (this.userData) this.userData.shopOrder = validOrder;
      try {
        const userRef = doc(db, 'owner-users', fbAuth.uid.value);
        await setDoc(userRef, { shopOrder: validOrder }, { merge: true });
      } catch (e) {
        console.error('[owner-v2] failed to persist shop order to user doc', e);
      }
    },

    // stores is a getter now — see getters block.

    /**
     * Link a new POS shop to the current Firebase owner account.
     * Calls POS /users/login directly and writes the resulting JWT into
     * Firestore under owner-users/{uid}.shops.{customerId}. The plaintext
     * password is never persisted. Active token is mirrored to
     * localStorage.mobile_token (separate from the desktop POS key).
     */
    async addShop(credentials: { username: string; password: string }) {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) throw new Error('Not authenticated');

      const { public: { apiBase } } = useRuntimeConfig();

      const response = await axios.post(`${apiBase}/users/login`, {
        UserName: credentials.username,
        Username: credentials.username,
        Password: credentials.password,
      }, { headers: { 'Content-Type': 'application/json' } });

      const { user, CustomerId, token } = response.data || {};
      if (!CustomerId || !token) {
        throw new Error('POS login did not return a valid shop credential');
      }

      const customerId = String(CustomerId);

      // Block linking shops that haven't migrated to app2 yet.
      try {
        const check = await $fetch<{ migrated?: boolean }>(
          'https://api-app2.niceloop.com/public/check-app2-version',
          { method: 'GET', query: { customerId } },
        );
        if (check?.migrated !== true) {
          const err: any = new Error('SHOP_NOT_MIGRATED');
          err.code = 'SHOP_NOT_MIGRATED';
          err.name = 'SHOP_NOT_MIGRATED';
          throw err;
        }
      } catch (e: any) {
        if (e?.code === 'SHOP_NOT_MIGRATED') throw e;
        // Fail closed: don't link a shop whose migration status we couldn't verify.
        console.warn('[owner-v2] check-app2-version failed', e);
        const err: any = new Error('SHOP_NOT_MIGRATED');
        err.code = 'SHOP_NOT_MIGRATED';
        throw err;
      }

      const role = (user?.Role ?? 0) as PosRole;
      const accessList = typeof user?.AccessList === 'string' && user.AccessList ? user.AccessList : 'all';
      // /users/login does not return the display shop name. Pull it from the
      // Firestore customers/{customerId} doc (kept fresh by the POS plugin).
      let shopName: string | undefined =
        user?.ShopName || user?.shopName || user?.CustomerName || undefined;
      if (!shopName) {
        try {
          const cSnap = await getDoc(doc(db, 'customers', customerId));
          shopName = (cSnap.exists() ? (cSnap.data() as any).name : undefined) || undefined;
        } catch (e) {
          console.warn('[owner-v2] failed to read customer name', customerId, e);
        }
      }

      const record: ShopRecord = {
        customerId,
        username: credentials.username,
        token,
        role,
        accessList,
        ...(shopName ? { shopName } : {}),
        linkedAt: new Date(),
        lastUsedAt: new Date(),
      };

      const userRef = doc(db, 'owner-users', fbAuth.uid.value);
      // setDoc + merge so this works even if the user doc wasn't created yet
      // (e.g. user signed in before the owner-users collection existed).
      await setDoc(userRef, {
        uid: fbAuth.uid.value,
        shops: { [customerId]: record },
        selectedCustomerId: customerId,
        createdAt: serverTimestamp(),
      }, { merge: true });

      if (!this.userData) {
        this.userData = {
          uid: fbAuth.uid.value,
          createdAt: new Date(),
          selectedCustomerId: customerId,
          shops: {},
        };
      }
      this.userData.shops[customerId] = record;
      this.userData.selectedCustomerId = customerId;
      this.selectedStoreId = customerId;
      this._applyShopAuth(customerId);
      this.subscribeAllStoresLiveSummary();
    },

    /**
     * Re-authenticate a previously linked shop after its token expired.
     * Overwrites the stored JWT but keeps the same customerId/username.
     */
    async reAuthShop(customerId: string, credentials: { username: string; password: string }) {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) throw new Error('Not authenticated');

      const { public: { apiBase } } = useRuntimeConfig();
      const response = await axios.post(`${apiBase}/users/login`, {
        UserName: credentials.username,
        Username: credentials.username,
        Password: credentials.password,
      }, { headers: { 'Content-Type': 'application/json' } });

      const { user, CustomerId, token } = response.data || {};
      if (!CustomerId || !token) {
        throw new Error('POS login did not return a valid shop credential');
      }
      if (String(CustomerId) !== customerId) {
        throw new Error('Credentials belong to a different shop');
      }

      const existing = this.userData?.shops[customerId];
      const accessList = typeof user?.AccessList === 'string' && user.AccessList
        ? user.AccessList
        : (existing?.accessList || 'all');
      // /users/login often omits ShopName; existing record may also lack it. Firestore
      // rejects `undefined` field values, so only include shopName when we actually have
      // one (matches addShop's conditional-spread handling).
      const shopName = user?.ShopName || existing?.shopName;
      const updated: ShopRecord = {
        customerId,
        username: credentials.username,
        token,
        role: (user?.Role ?? existing?.role ?? 0) as PosRole,
        accessList,
        ...(shopName ? { shopName } : {}),
        linkedAt: existing?.linkedAt || new Date(),
        lastUsedAt: new Date(),
      };

      const userRef = doc(db, 'owner-users', fbAuth.uid.value);
      await setDoc(userRef, { shops: { [customerId]: updated } }, { merge: true });

      if (this.userData) this.userData.shops[customerId] = updated;
      if (this.selectedStoreId === customerId) {
        this._applyShopAuth(customerId);
      }
    },

    /**
     * Mirror the active shop's JWT into localStorage.mobile_token so that
     * useApi (which is route-aware) picks it up on outbound requests.
     * Does NOT touch localStorage.token — that key belongs to desktop POS.
     */
    _applyShopAuth(storeId: string) {
      const shop = this.userData?.shops[storeId];
      if (import.meta.client) {
        if (shop?.token) {
          localStorage.setItem('mobile_token', shop.token);
        } else {
          localStorage.removeItem('mobile_token');
        }
      }
      const { setStoreId } = useApi();
      setStoreId(storeId);
    },

    /**
     * Renew every linked shop's JWT whose remaining lifetime is under the
     * threshold (< 7 days), then persist the fresh tokens back to Firestore.
     * Each shop is renewed independently via POST /users/renew with its own
     * Bearer token, so one failure (expired → needs re-link) never blocks the
     * others. Only tokens that are still valid can be renewed; already-expired
     * ones are skipped (the server 401s and the shop must be re-linked).
     */
    async renewExpiringShopTokens() {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value || !this.userData) return;

      const { public: { apiBase } } = useRuntimeConfig();
      const shops = Object.values(this.userData.shops);

      // Whole body is best-effort: a bad legacy record, a network blip, or a
      // Firestore write failure must never break init() (this runs fire-and-
      // forget). Worst case a shop simply isn't renewed and falls back to the
      // normal 401 → re-link flow when its token is next used.
      try {
        const results = await Promise.allSettled(shops.map(async (shop) => {
          // Legacy-account guards. Pre-JWT records may have:
          //   - no token stored at all → nothing to renew (needs re-link)
          //   - a plain (non-JWT) token with no exp → msUntilExpiry === null
          // Either way we must NOT call /users/renew: the server auth middleware
          // would 401 on a non-JWT bearer. Skip silently and let the shop keep
          // its existing token (the app already surfaces re-link when it 401s).
          if (!shop.token || typeof shop.token !== 'string') return false;
          const remaining = msUntilExpiry(shop.token);
          // null → legacy/non-JWT token; <=0 → already expired (renew would 401);
          // >= threshold → still fresh. Skip all three.
          if (remaining === null || remaining <= 0 || remaining >= SHOP_TOKEN_RENEW_THRESHOLD_MS) {
            return false;
          }
          const res = await axios.post(`${apiBase}/users/renew`, {}, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${shop.token}`,
            },
          });
          const newToken = res?.data?.token;
          // Only accept a well-formed JWT back — never overwrite a working token
          // with garbage (empty string, HTML error page, non-JWT) from an
          // unexpected response shape.
          if (typeof newToken !== 'string' || decodeJwtExp(newToken) === null) return false;
          // Mutate in place — `shop` is the live state object, so this updates
          // the store (and the `stores` getter) reactively.
          shop.token = newToken;
          // loadUserData's background server refresh may have replaced
          // userData meanwhile — write through to the live record as well.
          const live = this.userData?.shops[shop.customerId];
          if (live && live !== shop) live.token = newToken;
          return true;
        }));

        // Persist only the shops that actually got a new token, via targeted
        // dot-path updates so we don't rewrite the whole shops map.
        const updates: Record<string, string> = {};
        results.forEach((r, i) => {
          const shop = shops[i]!;
          if (r.status === 'fulfilled' && r.value) {
            updates[`shops.${shop.customerId}.token`] = shop.token;
          } else if (r.status === 'rejected') {
            console.warn('[owner-v2] token renew failed', shop.customerId, r.reason);
          }
        });

        if (Object.keys(updates).length) {
          const userRef = doc(db, 'owner-users', fbAuth.uid.value);
          await updateDoc(userRef, updates);
          // If the currently-selected shop's token changed, re-mirror it to
          // localStorage.mobile_token so outbound API calls use the fresh JWT.
          if (updates[`shops.${this.selectedStoreId}.token`]) {
            this._applyShopAuth(this.selectedStoreId);
          }
        }
      } catch (err) {
        console.warn('[owner-v2] renewExpiringShopTokens failed', err);
      }
    },

    async removeShop(storeId: string) {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) return;

      const userRef = doc(db, 'owner-users', fbAuth.uid.value);
      await updateDoc(userRef, { [`shops.${storeId}`]: deleteField() });

      if (this.userData) delete this.userData.shops[storeId];

      if (this.selectedStoreId === storeId) {
        const remaining = this.userData ? Object.keys(this.userData.shops) : [];
        this.selectedStoreId = remaining[0] || '';
        if (this.userData) this.userData.selectedCustomerId = this.selectedStoreId || null;
        await this.saveSelectedStore();

        if (this.selectedStoreId) {
          this._applyShopAuth(this.selectedStoreId);
        } else if (import.meta.client) {
          localStorage.removeItem('mobile_token');
        }
      }

      this.subscribeAllStoresLiveSummary();
    },

    async saveSelectedStore() {
      const fbAuth = useFirebaseAuth();
      if (!fbAuth.uid.value) return;

      const userRef = doc(db, 'owner-users', fbAuth.uid.value);
      await setDoc(userRef, { selectedCustomerId: this.selectedStoreId || null }, { merge: true });
    },

    selectStore(storeId: string) {
      // Selecting a shop (top-right switcher) only changes the API context.
      // It does NOT touch the dashboard tables_v3 subscription — that one is
      // driven exclusively by tapping a shop card on the Dashboard.
      // useOwnerDataservice resolves customerId/businessMode live from this store,
      // so changing selectedStoreId is enough for reports to target the new shop.
      this.selectedStoreId = storeId;
      if (this.userData) this.userData.selectedCustomerId = storeId;
      this.saveSelectedStore();
      this._applyShopAuth(storeId);
      useDataservice().setCustomerId(storeId);
    },

    async signOut() {
      this.unsubscribeAllLiveTables();
      this.unsubscribeAllStoresLiveSummary();

      const fbAuth = useFirebaseAuth();
      await fbAuth.signOut();
      this.userData = null;
      this.selectedStoreId = '';
      this.loaded = false;
      this.liveTables = {};
      this.liveTablesLoaded = false;

      const { setStoreId } = useApi();
      setStoreId('');
      if (import.meta.client) {
        localStorage.removeItem('mobile_token');
      }

      return navigateTo('/v2-owner-report-mobile/login');
    },

    getSelectedStoreCredentials() {
      const shop = this.userData?.shops[this.selectedStoreId];
      return shop ? {
        username: shop.username,
        token: shop.token,
        storeId: shop.customerId,
      } : null;
    },

    _extractGrandTotal(data: CustomerSummaryData): number {
      if (data.status === 'openning') {
        let total = 0;
        for (const key of Object.keys(data)) {
          if (key.startsWith('__')) {
            total += Number(data[key]) || 0;
          }
        }
        return total;
      } else {
        return Number(data.grandTotal) || 0;
      }
    },

    async fetchCustomerSummaryByRange(storeId: string, startDate: string, endDate?: string) {
      const end = endDate || startDate;

      try {
        const startTimestamp = Timestamp.fromDate(new Date(startDate + 'T00:00:00'));
        const endTimestamp = Timestamp.fromDate(new Date(end + 'T23:59:59'));

        const summaryCollection = collection(db, 'customers', storeId, 'summary');
        const q = query(
          summaryCollection,
          where('lastUpdate', '>=', startTimestamp),
          where('lastUpdate', '<=', endTimestamp)
        );

        const querySnapshot = await getDocs(q);
        const results: CustomerSummaryByDate = {};

        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data() as CustomerSummaryData;
          results[docSnapshot.id] = {
            ...data,
            _computedGrandTotal: this._extractGrandTotal(data),
          } as CustomerSummaryData;
        });

        const startObj = new Date(startDate);
        const endObj = new Date(end);
        const totalDays = Math.ceil((endObj.getTime() - startObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;

        const totalGrandTotal = Object.values(results).reduce((sum, data) => {
          return sum + ((data as CustomerSummaryData & { _computedGrandTotal?: number })?._computedGrandTotal || 0);
        }, 0);

        const summaryResult: CustomerSummaryResult = {
          shopId: storeId,
          dateRange: { start: startDate, end, totalDays },
          totalGrandTotal,
          datesWithData: Object.keys(results).length,
        };

        this.customerSummary[storeId] = { summaryData: results, summaryResult };

        return { summaryData: results, summaryResult };
      } catch (error) {
        console.error('Error fetching customer summary:', error);
        throw error;
      }
    },

    async fetchAllStoresCustomerSummaryByRange(startDate: string, endDate?: string) {
      this.customerSummaryLoading = true;
      try {
        await Promise.all(
          this.stores.map((store) =>
            this.fetchCustomerSummaryByRange(store.storeId, startDate, endDate)
          )
        );
      } catch (error) {
        console.error('Error fetching all stores customer summary:', error);
        throw error;
      } finally {
        this.customerSummaryLoading = false;
      }
    },

    async setDashboardPeriod(payload: {
      view: 'live' | 'by_date' | 'by_month' | 'by_year';
      date?: Date;
      month?: { month: number; year: number };
      year?: number;
    }) {
      this.dashboardView = payload.view;

      const today = dayjs();
      let start: dayjs.Dayjs;
      let end: dayjs.Dayjs;

      switch (payload.view) {
        case 'by_date':
          start = dayjs(payload.date).startOf('day');
          end = dayjs(payload.date).endOf('day');
          break;
        case 'by_month':
          start = dayjs(new Date(payload.month!.year, payload.month!.month)).startOf('month');
          end = dayjs(new Date(payload.month!.year, payload.month!.month)).endOf('month');
          break;
        case 'by_year':
          start = dayjs(new Date(payload.year!, 0, 1)).startOf('year');
          end = dayjs(new Date(payload.year!, 11, 31)).endOf('year');
          break;
        case 'live':
        default:
          start = today.startOf('day');
          end = today.endOf('day');
          break;
      }

      this.dashboardStartDate = start.format('YYYY-MM-DD');
      this.dashboardEndDate = end.format('YYYY-MM-DD');

      if (this.stores.length > 0) {
        if (payload.view === 'live') {
          await this.fetchLiveGrandTotals();
        } else {
          let dateParam: string;
          switch (payload.view) {
            case 'by_date':
              dateParam = dayjs(payload.date).format('YYYY-MM-DD');
              break;
            case 'by_month':
              dateParam = dayjs(new Date(payload.month!.year, payload.month!.month)).format('YYYY-MM');
              break;
            case 'by_year':
              dateParam = String(payload.year);
              break;
            default:
              dateParam = today.format('YYYY-MM-DD');
              break;
          }
          await this.fetchGrandTotals(dateParam);
        }
      }
    },

    async _fetchGrandTotalsForGroup(storeIds: string[], date: string): Promise<Record<string, GrandTotalStats>> {
      if (!storeIds.length || !date) return {};
      const customerId = storeIds.includes(this.selectedStoreId)
        ? this.selectedStoreId
        : storeIds[0];

      const { $api } = useApi();
      const response = await $api<{ data: Record<string, Partial<GrandTotalStats>> }>(
        '/dashboard/grandTotal',
        {
          method: 'POST',
          body: { customerId, list: storeIds, date },
        },
      );
      const data = response?.data || {};
      const out: Record<string, GrandTotalStats> = {};
      for (const id of storeIds) {
        const row = data[id];
        out[id] = row
          ? {
              grandTotal: Number(row.grandTotal) || 0,
              bills: Number(row.bills) || 0,
              voidedAmount: Number(row.voidedAmount) || 0,
              voidedBills: Number(row.voidedBills) || 0,
            }
          : emptyGrandTotalStats();
      }
      return out;
    },

    async fetchGrandTotals(date: string) {
      const storeIds = this.stores.map((s) => s.storeId).filter(Boolean);
      if (!storeIds.length || !date) return;

      this.grandTotalsLoading = true;
      try {
        this.grandTotals = await this._fetchGrandTotalsForGroup(storeIds, date);
      } catch (error) {
        console.error('Error fetching grand totals:', error);
        throw error;
      } finally {
        this.grandTotalsLoading = false;
      }
    },

    // Refetch grandTotal for a single shop using its businessMode-aware date.
    // Used by the realtime Firestore snapshot debounce so a shop refreshes
    // shortly after its live doc changes — without re-querying every shop.
    async fetchGrandTotalForStore(storeId: string) {
      if (!storeId) return;
      const mode = this.liveSummaries[storeId]?.businessMode;
      const date = computeBusinessDate(mode);
      try {
        const partial = await this._fetchGrandTotalsForGroup([storeId], date);
        this.grandTotals = { ...this.grandTotals, ...partial };
      } catch (error) {
        console.error('Error fetching grand total for store', storeId, error);
      }
    },

    // Live view: each shop's date depends on its businessMode (1 = nightclub:
    // subtract 7h before truncating to date). Shops with different computed
    // dates get separate requests; shops sharing a date are batched.
    async fetchLiveGrandTotals() {
      const storeIds = this.stores.map((s) => s.storeId).filter(Boolean);
      if (!storeIds.length) return;

      const now = dayjs();
      const groups = new Map<string, string[]>();
      for (const id of storeIds) {
        const mode = this.liveSummaries[id]?.businessMode;
        const date = computeBusinessDate(mode, now);
        const arr = groups.get(date);
        if (arr) arr.push(id);
        else groups.set(date, [id]);
      }

      this.grandTotalsLoading = true;
      try {
        const results = await Promise.all(
          Array.from(groups.entries()).map(([date, ids]) =>
            this._fetchGrandTotalsForGroup(ids, date),
          ),
        );
        this.grandTotals = Object.assign({}, ...results);
      } catch (error) {
        console.error('Error fetching live grand totals:', error);
        throw error;
      } finally {
        this.grandTotalsLoading = false;
      }
    },

    /**
     * Subscribe to a shop's tables_v3 stream. Idempotent — calling it again
     * for the same shop while already subscribed is a no-op (no flicker on
     * page navigation). Calling it with a different shop tears down the
     * previous subscription and starts a fresh one.
     */
    subscribeLiveTables(storeId: string) {
      if (this.subscribedTablesStoreId === storeId && this.liveTablesLoaded) {
        return;
      }
      this.unsubscribeAllLiveTables();

      this.subscribedTablesStoreId = storeId;
      this.liveTablesLoading = true;
      this.liveTablesLoaded = false;

      // Build the ref directly — do NOT call useRealtimeDatabase().setStoreId()
      // here. That storeId is a GLOBAL every POS ref builder reads at call
      // time; mutating it from the owner view repoints all POS publishers
      // (tables_v3, mobile sync, jobchain) at another customer's node when a
      // POS session lives in the same tab.
      const tablesV3Ref = rtdbRef(rtdb, `customer/${storeId}/tables_v3`);

      const unsub = onValue(tablesV3Ref, (snapshot) => {
        // onValue fires once with the initial state (even if empty), then on
        // every change — perfect for both "loading done" and live updates.
        this.liveTables = (snapshot.val() as LiveTablesData) || {};
        this.liveTablesLoading = false;
        this.liveTablesLoaded = true;
      }, (err) => {
        console.error('[owner-v2] tables_v3 subscription error', storeId, err);
        this.liveTablesLoading = false;
      });

      this._liveTablesUnsubscribers[storeId] = [unsub];
    },

    unsubscribeLiveTables(storeId: string) {
      const unsubscribers = this._liveTablesUnsubscribers[storeId];
      if (unsubscribers) {
        unsubscribers.forEach((unsub) => unsub());
        delete this._liveTablesUnsubscribers[storeId];
      }
    },

    unsubscribeAllLiveTables() {
      Object.keys(this._liveTablesUnsubscribers).forEach((storeId) => {
        this.unsubscribeLiveTables(storeId);
      });
      this.liveTables = {};
      this.liveTablesLoaded = false;
      this.liveTablesLoading = false;
      this.subscribedTablesStoreId = '';
    },

    /**
     * Subscribe to the per-store live aggregate doc (customers/{storeId})
     * which the POS-side jobs.live.plugin.ts keeps fresh. Used by the
     * mobile owner LIVE dashboard to render one card per linked shop.
     */
    subscribeStoreLiveSummary(storeId: string) {
      this.unsubscribeStoreLiveSummary(storeId);
      const ref = doc(db, 'customers', storeId);
      const unsub = onSnapshot(ref, (snap) => {
        const data = snap.exists() ? (snap.data() as StoreLiveDoc) : {};
        this.liveSummaries[storeId] = {
          name: data?.name,
          live: data?.live,
          businessMode: data?.businessMode,
          status: data?.status,
          opened: data?.opened,
          closed: data?.closed,
        };

        // Skip the initial snapshot — the page-mount fetch already covers it.
        // On every subsequent update, debounce 1200ms and refetch grandTotal
        // for this shop only (DynamoDB needs a moment to project the new
        // receipt event before our API can read it).
        if (!this._liveSummaryFirstSeen[storeId]) {
          this._liveSummaryFirstSeen[storeId] = true;
          return;
        }
        const existing = this._grandTotalRefetchTimers[storeId];
        if (existing) clearTimeout(existing);
        this._grandTotalRefetchTimers[storeId] = setTimeout(() => {
          delete this._grandTotalRefetchTimers[storeId];
          this.fetchGrandTotalForStore(storeId);
        }, 1200);
      }, (err) => {
        console.error('[liveSummary] snapshot error', storeId, err);
      });
      this._liveSummaryUnsubs[storeId] = unsub;
    },

    unsubscribeStoreLiveSummary(storeId: string) {
      const unsub = this._liveSummaryUnsubs[storeId];
      if (unsub) {
        unsub();
        delete this._liveSummaryUnsubs[storeId];
      }
      const timer = this._grandTotalRefetchTimers[storeId];
      if (timer) {
        clearTimeout(timer);
        delete this._grandTotalRefetchTimers[storeId];
      }
      delete this._liveSummaryFirstSeen[storeId];
    },

    subscribeAllStoresLiveSummary() {
      // Drop any subscriptions for stores no longer linked
      const linked = new Set(this.stores.map(s => s.storeId));
      for (const sid of Object.keys(this._liveSummaryUnsubs)) {
        if (!linked.has(sid)) this.unsubscribeStoreLiveSummary(sid);
      }
      // Subscribe (idempotent — subscribeStoreLiveSummary tears down first)
      for (const s of this.stores) {
        this.subscribeStoreLiveSummary(s.storeId);
      }
    },

    unsubscribeAllStoresLiveSummary() {
      for (const sid of Object.keys(this._liveSummaryUnsubs)) {
        this.unsubscribeStoreLiveSummary(sid);
      }
      this.liveSummaries = {};
    },
  },

  getters: {
    stores(state): Store[] {
      if (!state.userData) return [];
      // Apply the owner's saved display order. shopOrder lists customerIds the
      // owner arranged; ids that were since unlinked are skipped, and any shop
      // not in the list (e.g. just added) is appended in natural (insertion)
      // order. When there is no saved order, keep natural order untouched.
      const shopRecords = Object.values(state.userData.shops);
      const order = state.userData.shopOrder;
      const orderedRecords = order && order.length
        ? [
            ...order
              .map(id => shopRecords.find(s => s.customerId === id))
              .filter((s): s is ShopRecord => !!s),
            ...shopRecords.filter(s => !order.includes(s.customerId)),
          ]
        : shopRecords;
      return orderedRecords.map((shop) => ({
        storeId: shop.customerId,
        // Prefer the LIVE name from customers/{customerId} (kept fresh by the
        // POS plugin) — the shopName stored at link time can go stale if the
        // owner renames the shop. Fall back to the stored name, then a stub.
        name: state.liveSummaries[shop.customerId]?.name
          || shop.shopName
          || `Shop: ${shop.customerId}`,
        username: shop.username,
        token: shop.token || '',
        role: (shop.role ?? 0) as PosRole,
        accessList: shop.accessList || 'all',
        needsReLink: !shop.token,
      }));
    },
    selectedStore(): Store | undefined {
      return this.stores.find(s => s.storeId === this.selectedStoreId);
    },
    // Per-shop business-day cutoff for the selected shop. owner-mobile never loads
    // the shop's system-envs into useSettingsStore, so reports must source
    // businessMode from the live Firestore doc instead of the (empty) settings store.
    // 0 = calendar day, 1 = late-night (-7h). Falls back to 0 until the live doc arrives.
    selectedBusinessMode(): number {
      return Number(this.liveSummaries[this.selectedStoreId]?.businessMode) || 0;
    },
    isAuthenticated: (state) => !!state.userData,
    currentShop(): Store | undefined {
      return this.stores.find(s => s.storeId === this.selectedStoreId);
    },
    currentRole(): PosRole {
      return (this.currentShop?.role ?? 0) as PosRole;
    },
    currentAccessList(): string[] {
      const raw = this.currentShop?.accessList || 'all';
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    },
    currentHasAllAccess(): boolean {
      return this.currentAccessList.includes('all');
    },
    // Role 0/2 are unrestricted on owner-mobile. Role 1 must have 'all' or 'report'.
    canAccessReport(): (storeId: string) => boolean {
      const shops = this.userData?.shops || {};
      return (storeId: string) => {
        const shop = shops[storeId];
        if (!shop) return false;
        if (shop.role !== 1) return true;
        const raw = shop.accessList || 'all';
        const list = raw.split(',').map((s) => s.trim()).filter(Boolean);
        return list.includes('all') || list.includes('report');
      };
    },
    currentCanAccessReport(): boolean {
      if (!this.selectedStoreId) return false;
      return this.canAccessReport(this.selectedStoreId);
    },
    // Phase 1: role 1 (custom) is treated as full access until permission-string parsing lands.
    canAccessAll(): boolean {
      const r = this.currentRole;
      return r === 2 || r === 1;
    },
    currentShopNeedsReLink(): boolean {
      return !!this.currentShop?.needsReLink;
    },
    activeTables: (state) => {
      return Object.values(state.liveTables).filter(
        (table) => table.items && table.items.length > 0
      );
    },
    totalGuests: (state) => {
      return Object.values(state.liveTables).reduce(
        (sum, table) => sum + (table.guests || 0),
        0
      );
    },
    totalPendingAmount: (state) => {
      return Object.values(state.liveTables).reduce(
        (sum, table) => sum + (table.billing?.subTotal || 0),
        0
      );
    },
  },
});
